import * as React from 'react'
import * as Redux from 'react-redux'
import { styled, css } from 'next-yak'

import { t } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as env from '@owl-nest/config'

import * as plume from '@ulule/owl-kit-components/next'

import { RootState } from '../../../reducers'
import * as projectModel from '../../models/project'
import { SplashTitle } from './components/SplashTitle'
import { HeroSvg } from './HeroSvg'

export function ContextMembership(): React.ReactElement {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const userConfig = env.useUserEnv()
  const lang = userConfig.lang

  const title = models.project.name<projectModel.ExtraFields>(project)
  const mainImage =
    project?.coming_soon?.image?.[lang]?.versions?.['large']?.url ??
    project?.coming_soon?.image?.[lang]?.versions?.['full']?.url ??
    models.project.mainImage<projectModel.ExtraFields>(project) ??
    models.project.mainImage<projectModel.ExtraFields>(project, 'full')

  const ownerDescription = models.project.ownerDescription(project, lang)

  const isDesktop = plume.utils.isTabletAndMore()

  const avatar = models.project.avatarImage(project, lang, '180x180')

  return (
    <MembershipContextWrapper>
      <StyledHeroSvg />

      <Wrapper>
        <MembershipBanner>
          <SplashTitle text={t('Coming soon!')} />

          {mainImage && <Banner $backgroundImage={mainImage} />}

          {isDesktop ? (
            <plume.UserLabel
              avatar={avatar}
              avatarSize="extra-large"
              isPublic={project.owner.is_public}
              href={project.owner.absolute_url}
            />
          ) : (
            <plume.UserLabel
              avatar={avatar}
              avatarSize="large"
              isPublic={project.owner.is_public}
              href={project.owner.absolute_url}
            />
          )}
        </MembershipBanner>

        <MembershipTitle>
          <plume.styles.heading.L asDesktop="XL">{title}</plume.styles.heading.L>
          {ownerDescription && (
            <OwnerDescription>
              <plume.styles.copy.M.light dangerouslySetInnerHTML={{ __html: ownerDescription }} />
            </OwnerDescription>
          )}
        </MembershipTitle>
      </Wrapper>
    </MembershipContextWrapper>
  )
}

type BannerProps = {
  $backgroundImage: string
}

const OwnerDescription = styled(plume.WysiwygContent)`
  max-width: 651px;
  margin: 0 auto;

  p {
    margin: 0;
  }

  ${plume.styles.copy.S.regular} > p,
  ${plume.styles.copy.S.regular} strong {
    font-size: inherit;
    margin: 0;

    * {
      margin: 0;
    }
  }
`

const StyledHeroSvg = styled(HeroSvg)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: none;

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    display: block;
  }
`

const MembershipContextWrapper = styled.div`
  position: relative;
  max-width: 1210px;
  margin: 0px 0 32px 0px;

  ${SplashTitle} {
    position: absolute;
    display: block;
    left: -1px;
    margin: 10px auto 25px auto;
    top: -38px;
    width: fit-content;
    z-index: ${plume.ZINDEX.ABOVE};
  }

  ${plume.styles.image.Avatar} {
    border-width: 4px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin: 86px 0 50px 0px;
    width: 100%;

    ${SplashTitle} {
      top: -41px;
      left: -1px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    ${SplashTitle} {
      top: -41px;
      left: -110px;
    }
  }
`

const Wrapper = styled.div`
  margin: 32px auto 0px auto;
  max-width: 884px;
  position: relative;
  width: 100%;
`

const MembershipBanner = styled.div`
  position: relative;
  margin: 32px 16px 50px 16px;

  ${plume.UserLabel} {
    bottom: -45px;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin: 40px auto 108px auto;

    ${plume.UserLabel} {
      bottom: -91px;
      left: 50%;
      position: absolute;
      transform: translateX(-50%);
    }
  }
`

const MembershipTitle = styled.div`
  gap: 24px;
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 68px 16px 0px 16px;
  text-align: center;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    gap: 32px;
    margin: 0px;
  }
`

const Banner = styled.img<BannerProps>`
  align-items: center;
  aspect-ratio: 63/13;
  background-color: ${({ theme }) => `${(theme as any)?.colors?.background ?? plume.COLORS.PRIMARY_SAND_200}`};
  background-position: center;
  background-size: cover;
  border-radius: 4px;
  display: flex;
  height: auto;
  justify-content: center;
  position: relative;
  width: 100%;

  ${({ $backgroundImage }) => {
    return css`
      background-image: ${() => `url('${$backgroundImage}')`};
      background-position: center;
      background-size: cover;

      @media screen and ${plume.BREAKPOINTS.TABLET} {
        background-image: ${() => `url('${$backgroundImage}')`};
        aspect-ratio: 884 / 182.32;
        height: auto;
      }
    `
  }};
`
